import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tab,
  Tabs,
  Checkbox
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PaymentIcon from '@mui/icons-material/Payment';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
// Removed unused AutorenewIcon import

// Mock plans data
const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: '$29',
    period: 'monthly',
    description: 'Essential features for beginners',
    isCurrent: false,
    isPopular: false,
    annualPrice: '$290',
    features: [
      { text: '3 Active Strategies', included: true },
      { text: 'Technical Analysis Tools', included: true },
      { text: 'Backtesting (Limited)', included: true },
      { text: 'Email Support', included: true },
      { text: 'AI Trading Signals', included: false },
      { text: 'Advanced Risk Management', included: false },
      { text: 'API Access', included: false },
      { text: 'Phone Support', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$79',
    period: 'monthly',
    description: 'Advanced features for serious traders',
    isCurrent: true,
    isPopular: true,
    annualPrice: '$790',
    features: [
      { text: '10 Active Strategies', included: true },
      { text: 'Technical Analysis Tools', included: true },
      { text: 'Unlimited Backtesting', included: true },
      { text: 'Priority Email Support', included: true },
      { text: 'AI Trading Signals', included: true },
      { text: 'Advanced Risk Management', included: true },
      { text: 'API Access', included: false },
      { text: 'Phone Support', included: false },
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$199',
    period: 'monthly',
    description: 'Maximum power for professional traders',
    isCurrent: false,
    isPopular: false,
    annualPrice: '$1,990',
    features: [
      { text: 'Unlimited Active Strategies', included: true },
      { text: 'Advanced Technical Analysis', included: true },
      { text: 'Unlimited Backtesting', included: true },
      { text: '24/7 Priority Support', included: true },
      { text: 'Advanced AI Trading Signals', included: true },
      { text: 'Advanced Risk Management', included: true },
      { text: 'Full API Access', included: true },
      { text: 'Dedicated Account Manager', included: true },
    ]
  }
];

// Mock billing history
const billingHistory = [
  { id: 'INV-2025-0042', date: '2025-05-01', amount: '$79.00', status: 'Paid', plan: 'Pro (Monthly)' },
  { id: 'INV-2025-0028', date: '2025-04-01', amount: '$79.00', status: 'Paid', plan: 'Pro (Monthly)' },
  { id: 'INV-2025-0015', date: '2025-03-01', amount: '$79.00', status: 'Paid', plan: 'Pro (Monthly)' },
  { id: 'INV-2025-0003', date: '2025-02-01', amount: '$79.00', status: 'Paid', plan: 'Pro (Monthly)' },
  { id: 'INV-2024-0091', date: '2025-01-01', amount: '$79.00', status: 'Paid', plan: 'Pro (Monthly)' },
  { id: 'INV-2024-0076', date: '2024-12-01', amount: '$29.00', status: 'Paid', plan: 'Basic (Monthly)' },
];

// Mock payment methods
const paymentMethods = [
  { id: 1, type: 'visa', last4: '4242', expiry: '04/28', isDefault: true },
  { id: 2, type: 'mastercard', last4: '5555', expiry: '09/26', isDefault: false },
  { id: 3, type: 'klarna', last4: '', expiry: '', isDefault: false },
  { id: 4, type: 'swish', last4: '9999', expiry: '', isDefault: false },
];

function Plans() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openBillingDialog, setOpenBillingDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);
  // Payment method state - commenting out for now as it's not being used yet
  // We'll use this when implementing actual payment processing
  // const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [upgradeDetails, setUpgradeDetails] = useState(null);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleBillingCycleChange = (event) => {
    setBillingCycle(event.target.value);
  };
  
  const handleUpgradeClick = (plan) => {
    setUpgradeDetails(plan);
    setOpenUpgradeDialog(true);
  };
  
  const handleDowngradeClick = (plan) => {
    setUpgradeDetails(plan);
    setOpenUpgradeDialog(true);
  };
  
  const confirmUpgrade = () => {
    // Handle the upgrade/downgrade process here
    alert(`Your plan has been changed to ${upgradeDetails.name}. The changes will be reflected in your next billing cycle.`);
    setOpenUpgradeDialog(false);
  };
  
  const handleAddPaymentMethod = () => {
    // This would be implemented with a payment processor integration
    alert('New payment method added successfully!');
    setOpenPaymentDialog(false);
  };
  
  const getCardIcon = (type) => {
    switch(type) {
      case 'visa':
        return '💳 Visa';
      case 'mastercard':
        return '💳 Mastercard';
      case 'klarna':
        return '🛒 Klarna';
      case 'swish':
        return '📱 Swish';
      default:
        return '💳';
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Subscription Plans</Typography>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Plans & Pricing" />
        <Tab label="Billing History" />
        <Tab label="Payment Methods" />
      </Tabs>
      
      {/* Plans & Pricing Tab */}
      {tabValue === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <FormControl>
              <RadioGroup
                row
                value={billingCycle}
                onChange={handleBillingCycleChange}
              >
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly Billing" />
                <FormControlLabel value="annual" control={<Radio />} label="Annual Billing" />
              </RadioGroup>
            </FormControl>
            {billingCycle === 'annual' && (
              <Chip 
                label="Save 20%" 
                color="success" 
                size="small" 
                sx={{ ml: 1, height: 24, mt: 0.5 }} 
              />
            )}
          </Box>
          
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card 
                  variant={plan.isPopular ? 'elevation' : 'outlined'} 
                  elevation={plan.isPopular ? 6 : 0}
                  sx={{
                    position: 'relative',
                    height: '100%',
                    ...(plan.isPopular && {
                      borderTop: '4px solid',
                      borderColor: 'primary.main',
                    })
                  }}
                >
                  {plan.isPopular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 24,
                      }}
                    />
                  )}
                  
                  <CardContent>
                    <Typography variant="h5" component="div" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {plan.description}
                    </Typography>
                    
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h3" component="div">
                        {billingCycle === 'monthly' ? plan.price : plan.annualPrice}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per {billingCycle === 'monthly' ? 'month' : 'year'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {feature.included ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="disabled" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.text} 
                            primaryTypographyProps={{
                              ...(feature.included ? {} : { color: 'text.disabled' })
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    {plan.isCurrent ? (
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        disabled
                        startIcon={<CheckIcon />}
                      >
                        Current Plan
                      </Button>
                    ) : (
                      <Button 
                        fullWidth 
                        variant="contained" 
                        color={plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.isCurrent) ? 'primary' : 'secondary'}
                        onClick={() => {
                          const currentPlanIndex = plans.findIndex(p => p.isCurrent);
                          const thisPlanIndex = plans.findIndex(p => p.id === plan.id);
                          
                          if (thisPlanIndex > currentPlanIndex) {
                            handleUpgradeClick(plan);
                          } else {
                            handleDowngradeClick(plan);
                          }
                        }}
                        startIcon={plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.isCurrent) ? 
                          <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                      >
                        {plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.isCurrent) ? 
                          'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Need a custom plan?</Typography>
              <Typography variant="body2" paragraph>
                For businesses with specific requirements, we offer tailored enterprise solutions.
                Contact our sales team to discuss your needs.
              </Typography>
              <Button variant="outlined">Contact Sales</Button>
            </Paper>
          </Box>
        </>
      )}
      
      {/* Billing History Tab */}
      {tabValue === 1 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.id}</TableCell>
                    <TableCell>{invoice.date}</TableCell>
                    <TableCell>{invoice.plan}</TableCell>
                    <TableCell align="right">{invoice.amount}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={invoice.status} 
                        color={invoice.status === 'Paid' ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button 
                        size="small" 
                        startIcon={<ReceiptIcon />} 
                        onClick={() => setOpenBillingDialog(true)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Button 
              onClick={() => setOpenBillingDialog(true)}
              startIcon={<ReceiptIcon />}
            >
              View All Invoices
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Payment Methods Tab */}
      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Payment Methods</Typography>
          <List>
            {paymentMethods.map((method) => (
              <React.Fragment key={method.id}>
                <ListItem
                  secondaryAction={
                    <Box>
                      {method.isDefault && (
                        <Chip 
                          label="Default" 
                          size="small" 
                          color="primary" 
                          sx={{ mr: 1 }} 
                        />
                      )}
                      <Button 
                        size="small" 
                        variant="outlined"
                        disabled={method.isDefault}
                        onClick={() => {
                          // Set as default logic would go here
                          alert('Payment method set as default');
                        }}
                      >
                        Set Default
                      </Button>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <CreditCardIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={getCardIcon(method.type)} 
                    secondary={`•••• ${method.last4} | Expires ${method.expiry}`} 
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
          
          <Box sx={{ mt: 3 }}>
            <Button 
              variant="contained" 
              startIcon={<CreditCardIcon />}
              onClick={() => setOpenPaymentDialog(true)}
            >
              Add Payment Method
            </Button>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Billing Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Company Name" 
                  fullWidth 
                  margin="normal" 
                  value="John Doe Trading LLC" 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Email" 
                  fullWidth 
                  margin="normal" 
                  value="billing@johndoe.com" 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Address" 
                  fullWidth 
                  margin="normal" 
                  value="123 Trading Street, New York, NY 10001, USA" 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="VAT/Tax ID" 
                  fullWidth 
                  margin="normal" 
                  value="US123456789" 
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined">Update Billing Information</Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Invoice Dialog */}
      <Dialog open={openBillingDialog} onClose={() => setOpenBillingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Invoice Details</Typography>
            <IconButton onClick={() => setOpenBillingDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5">Invoice #INV-2025-0042</Typography>
                <Typography variant="body2" color="text.secondary">
                  Date: May 1, 2025
                </Typography>
                <Chip 
                  label="Paid" 
                  color="success" 
                  size="small" 
                  sx={{ mt: 1 }} 
                />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6">AI Trading System</Typography>
                <Typography variant="body2">123 AI Street</Typography>
                <Typography variant="body2">San Francisco, CA 94107</Typography>
                <Typography variant="body2">support@aitradingsystem.com</Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Billed To</Typography>
              <Typography variant="body2">John Doe Trading LLC</Typography>
              <Typography variant="body2">123 Trading Street</Typography>
              <Typography variant="body2">New York, NY 10001</Typography>
              <Typography variant="body2">billing@johndoe.com</Typography>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Period</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Pro Plan Subscription</TableCell>
                    <TableCell align="right">May 1 - May 31, 2025</TableCell>
                    <TableCell align="right">$79.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right">Subtotal</TableCell>
                    <TableCell align="right">$79.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right">Tax (0%)</TableCell>
                    <TableCell align="right">$0.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right"><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>$79.00</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 3, bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Payment Information</Typography>
              <Typography variant="body2">
                Paid with Credit Card (Visa ending in 4242)
              </Typography>
              <Typography variant="body2">
                Transaction ID: ch_3NkM9P2eZvKYlo2C1MFjHSVp
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<ReceiptIcon />}
            onClick={() => {
              alert('Invoice downloaded as PDF');
              setOpenBillingDialog(false);
            }}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Payment Method Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add Payment Method</Typography>
            <IconButton onClick={() => setOpenPaymentDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Choose your preferred payment method for subscription payments.
          </Typography>
          
          <Tabs value={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tab label="Credit Card" icon={<CreditCardIcon />} />
            <Tab label="Klarna" icon={<PaymentIcon />} />
            <Tab label="Swish" icon={<PhoneIphoneIcon />} />
          </Tabs>
          
          {/* Credit Card Form */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                label="Card Number" 
                fullWidth 
                placeholder="1234 5678 9012 3456" 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Expiration Date" 
                fullWidth 
                placeholder="MM/YY" 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="CVC" 
                fullWidth 
                placeholder="123" 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Cardholder Name" 
                fullWidth 
                placeholder="John Doe" 
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Alternative Payment Methods
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Button 
                  variant="outlined" 
                  startIcon={<PaymentIcon />} 
                  fullWidth
                  onClick={() => alert('Redirecting to Klarna checkout')}
                >
                  Pay with Klarna
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button 
                  variant="outlined" 
                  startIcon={<PhoneIphoneIcon />} 
                  fullWidth
                  onClick={() => alert('Opening Swish mobile app')}
                >
                  Pay with Swish
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={true} />}
              label="Set as default payment method"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddPaymentMethod}
          >
            Add Payment Method
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Upgrade/Downgrade Plan Dialog */}
      <Dialog open={openUpgradeDialog} onClose={() => setOpenUpgradeDialog(false)} maxWidth="sm" fullWidth>
        {upgradeDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? 
                    `Upgrade to ${upgradeDetails.name} Plan` : 
                    `Downgrade to ${upgradeDetails.name} Plan`}
                </Typography>
                <IconButton onClick={() => setOpenUpgradeDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? (
                <Typography variant="body2" paragraph>
                  You are about to upgrade from Pro to {upgradeDetails.name} Plan. Your new plan will take effect immediately, and you'll be charged the prorated difference for the remainder of your billing cycle.
                </Typography>
              ) : (
                <Typography variant="body2" paragraph>
                  You are about to downgrade from Pro to {upgradeDetails.name} Plan. Your new plan will take effect at the end of your current billing cycle on June 1, 2025.
                </Typography>
              )}
              
              <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Plan Changes</Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Current Plan:</Typography>
                  <Typography variant="body2">Pro ({billingCycle === 'monthly' ? '$79/month' : '$790/year'})</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">New Plan:</Typography>
                  <Typography variant="body2">
                    {upgradeDetails.name} ({billingCycle === 'monthly' ? 
                      upgradeDetails.price + '/month' : 
                      upgradeDetails.annualPrice + '/year'})
                  </Typography>
                </Box>
                
                {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="body2"><strong>Today's charge:</strong></Typography>
                    <Typography variant="body2"><strong>$40.00</strong></Typography>
                  </Box>
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? (
                  "You'll be charged the prorated amount today using your selected payment method."
                ) : (
                  "You'll continue to have access to your current plan features until the end of your billing cycle."
                )}
              </Typography>
              
              {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Payment Method</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Button 
                        variant="outlined" 
                        startIcon={<CreditCardIcon />} 
                        fullWidth
                        size="small"
                      >
                        Credit Card •••• 4242
                      </Button>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Button 
                        variant="outlined" 
                        startIcon={<PaymentIcon />} 
                        fullWidth
                        size="small"
                      >
                        Klarna
                      </Button>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Button 
                        variant="outlined" 
                        startIcon={<PhoneIphoneIcon />} 
                        fullWidth
                        size="small"
                      >
                        Swish
                      </Button>
                    </Grid>
                  </Grid>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenUpgradeDialog(false)}>Cancel</Button>
              <Button 
                variant="contained" 
                color={plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? 'primary' : 'secondary'}
                startIcon={plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? 
                  <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                onClick={confirmUpgrade}
              >
                {plans.findIndex(p => p.id === upgradeDetails.id) > plans.findIndex(p => p.isCurrent) ? 
                  'Confirm Upgrade' : 'Confirm Downgrade'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default Plans;
